import React, { useRef, useCallback, useState, useEffect } from 'react';
import { FormHandles } from '@unform/core';
import { addDays } from 'date-fns';
import { Form } from '@unform/web';
import { useSelector, useDispatch } from 'react-redux';
import PerfectScrollbar from 'react-perfect-scrollbar';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import {
  FiChevronRight,
  FiCopy,
  FiPlus,
  FiSearch,
  FiTrash2,
} from 'react-icons/fi';
import { Calendar, DateObject } from 'react-multi-date-picker';
import { useParams } from 'react-router-dom';

import { Setor } from 'utils/variables';
import { Cargos, isEleicoesMunicipais } from 'utils/eleicoes';
import { useModal } from 'hooks/modal';
import { show } from 'store/modules/dialog/actions';
import api from 'services/api';
import getValidationErrors from 'services/getValidationErros';
import { ClienteItem, MapaMidiaItem } from '../@types';
import { Container } from '../styles';
import { Content, Tabs, ModalContent, MapaList } from './styles';
import FormView from './FormView';

import Button from 'components/Button';
import Input from 'components/Input';
import Card from 'components/Card';
import Select from 'components/Select';
import DatePicker from 'components/DatePicker';
import TextArea from 'components/TextArea';
import ReactDropzoneInput from 'components/DropZone';
import Tooltip from 'components/Tooltip';

interface MapaMidiaSubmit {
  cargo: string;
  recebido_at: string;
  observacao: string;
  titulo: string;
  datas: string[];
  file: any;
}

export default function MapaMidia() {
  /**
   * REDUX
   */
  const dispatch = useDispatch();
  const { mapaId } = useParams<{ mapaId: string }>();

  const { emissora, profile } = useSelector((state: any) => state.usuario);
  /**
   * USE STATE
   */
  const [loadingCliente, setLoadingCliente] = useState(false);
  const [loadingMapas, setLoadingMapas] = useState(false);
  const [initialData, setInitialData] = useState({});
  const [, setLoadingMapaForm] = useState(false);
  const [clientes, setClientes] = useState<ClienteItem[]>([]);
  const [filteredClientes, setFilteredClientes] = useState<ClienteItem[]>([]);
  const [mapas, setMapas] = useState<MapaMidiaItem[]>([]);
  const [selectedCliente, setSelectedCliente] = useState({} as ClienteItem);
  const [selectedMapa, setSelectedMapa] = useState({} as MapaMidiaItem);
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [selectedDatesTarde, setSelectedDatesTarde] = useState<string[]>([]);
  const [selectedDatesNoite, setSelectedDatesNoite] = useState<string[]>([]);
  const [activeIndex, setActiveIndex] = useState(
    profile.setor === Setor.PROGRAMACAO ? 2 : 1,
  );
  const [isBloco, setIsBloco] = useState(profile.setor === Setor.PROGRAMACAO);
  const [, setPercent] = useState(0);

  /**
   * Hooks
   */
  const { showModal, hideModal } = useModal();
  /**
   * USE REF
   */
  const searchFormRef = useRef<FormHandles>(null);
  const mapaFormRef = useRef<FormHandles>(null);

  /**
   * USE CALLBACK
   */

  const handleSearch = useCallback(() => {
    try {
      if (searchFormRef && searchFormRef.current) {
        const filtro = searchFormRef.current.getFieldValue('search');
        setFilteredClientes(
          clientes.filter(c =>
            c.mnemonico.toUpperCase().includes(filtro.toUpperCase()),
          ),
        );
      }
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    }
  }, [clientes]);

  const renderDisableMapa = useCallback(
    (id: number) => {
      const handleSubmit = async () => {
        try {
          const response = await api.put(`/eleicoes/mapademidia/${id}`);
          setSelectedMapa(response.data);
          setMapas(mapas.map(m => (m.id === id ? response.data : m)));
          toast.success(`Mapa desabilitado`);
          hideModal();
        } catch (error) {
          if (error instanceof Error) {
            toast.error(error.message);
          }
        }
      };
      return (
        <ModalContent>
          <Button onClick={() => handleSubmit()} color="primary">
            CONFIRMAR
          </Button>
        </ModalContent>
      );
    },
    [hideModal, mapas],
  );

  const handleDisableMapa = useCallback(
    (mapa: MapaMidiaItem) => {
      try {
        if (mapa.id)
          showModal({
            title: `Desabilitar Mapa de Mídia ${mapa.originalfilename}?`,
            component: renderDisableMapa(mapa.id),
          });
      } catch (error) {
        console.log(error);
      }
    },
    [renderDisableMapa, showModal],
  );
  const handleSubmit = useCallback(
    async (data: MapaMidiaSubmit) => {
      setLoadingMapaForm(true);
      console.log(mapaFormRef.current);
      if (mapaFormRef.current) {
        try {
          mapaFormRef.current.setErrors({});

          const schema = Yup.object().shape({
            cargo: Yup.string().required('Preencha o cargo'),
            datas: Yup.array().required('Selecione a(s) data(s) de exibição'),
            file: Yup.array()
              .nullable()
              .required('Arquivo de Mapa de Mídia Obrigatório'),
            recebido_at: Yup.date()
              .required('Informe a data e horário de recebimento')
              .typeError('Informe a data de recebimento'),
            ...(isBloco && {
              titulo: Yup.string().required('Informe o título'),
              observacao: Yup.string(),
            }),
          });

          await schema.validate(
            {
              ...data,
              datas:
                selectedDates.length > 0
                  ? selectedDates
                  : selectedDatesTarde.length > 0
                  ? selectedDatesTarde
                  : selectedDatesNoite,
            },
            { abortEarly: false },
          );

          let datas_formatted;
          if (isBloco) {
            datas_formatted = selectedDatesTarde
              .map(d => ({
                data: d,
                turno: 'tarde',
              }))
              .concat(
                selectedDatesNoite.map(d => ({
                  data: d,
                  turno: 'noite',
                })),
              );
          } else {
            datas_formatted = selectedDates.map(d => ({
              data: d,
              turno: null,
            }));
          }

          const formData = new FormData();
          formData.append('cargo', data.cargo);
          formData.append('recebido_at', data.recebido_at);
          formData.append('emissora_id', emissora.value);
          formData.append('file', data.file[0]);
          formData.append('cliente_id', selectedCliente.id.toString());
          formData.append('datas', JSON.stringify(datas_formatted));
          formData.append('tipo', isBloco ? 'bloco' : 'insercao');
          if (isBloco) {
            if (data.observacao) formData.append('observacao', data.observacao);
            formData.append('titulo', data.titulo);
          }

          const response = await api.post<MapaMidiaItem[]>(
            '/eleicoes/mapademidia',
            formData,
            {
              timeout: 30000,
              onUploadProgress: function(progressEvent) {
                const percentCompleted = progressEvent.total
                  ? Math.round(
                      (progressEvent.loaded * 100) / progressEvent.total,
                    )
                  : 0;
                setPercent(percentCompleted);
              },
            },
          );

          if (response && response.data) {
            setMapas(response.data);
            toast.success('Mapa de mídia adicionado');
          }

          setSelectedMapa({ new: false });
          setSelectedDates([]);
          setSelectedDatesTarde([]);
          setSelectedDatesNoite([]);
          setLoadingMapaForm(false);
        } catch (err) {
          if (err instanceof Yup.ValidationError) {
            const errors = getValidationErrors(err);
            mapaFormRef.current.setErrors(errors);
            toast.error(err.message);
            return;
          } else if (err instanceof Error) {
            toast.error(err.message);
          } else if (
            !/^[a-zA-Z0-9- _.áàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ()]*$/.test(
              data.file[0].path,
            )
          ) {
            toast.error('Nome do arquivo contém caracteres especiais');
          } else {
            toast.error('Erro ao processar o mapa de mídia');
          }
          setLoadingMapaForm(false);
        }
      }
    },
    [
      emissora.value,
      isBloco,
      selectedCliente.id,
      selectedDates,
      selectedDatesTarde,
      selectedDatesNoite,
    ],
  );

  const handleSelectClient = useCallback(
    async cliente => {
      setLoadingMapas(true);
      try {
        const response = await api.get<MapaMidiaItem[]>(
          '/eleicoes/mapademidia',
          {
            params: {
              cliente_id: cliente.id,
              emissora_id: emissora.value,
              tipo: isBloco ? 'bloco' : 'insercao',
            },
          },
        );
        setSelectedCliente(cliente);
        setSelectedMapa({});
        if (response && response.data) setMapas(response.data);
        else toast.error('Falha de comunicação com o sistema');
      } catch (error) {
        if (error instanceof Error) toast.error(error.message);
        else console.log(error);
      } finally {
        setLoadingMapas(false);
      }
    },
    [emissora.value, isBloco],
  );

  const handleNewMapa = useCallback(() => {
    setSelectedMapa({ new: true });
  }, []);

  const handleSelectMapa = useCallback(async mapa => {
    try {
      setLoadingMapaForm(true);
      const response = await api.get<MapaMidiaItem>(
        `/eleicoes/mapademidia/${mapa.id}`,
      );
      if (response && response.data) {
        setSelectedMapa(response.data);
      } else toast.error('Falha de comunicação com o sistema');
    } catch (error) {
      if (error instanceof Error) toast.error(error.message);
      else console.log(error);
    } finally {
      setLoadingMapaForm(false);
    }
  }, []);

  const handleCopy = useCallback(selectedMapa => {
    setInitialData({
      cargo: Cargos.find(c => c.value === selectedMapa.cargo),
      recebido_at: new Date(selectedMapa.recebido_at),
    });
    setSelectedMapa({ new: true });
  }, []);

  const handleIsBloco = useCallback(bloco => {
    setMapas([]);
    setSelectedCliente({} as ClienteItem);
    setSelectedMapa({});
    setIsBloco(bloco);
    setActiveIndex(bloco ? 2 : 1);
  }, []);

  /**
   * USE EFFECT
   */
  useEffect(() => {
    if (!mapaId) {
      const options = {
        type: 'warning',
        contentHTML: `<h4>ATENÇÃO<h4><h1>PRAÇA SELECIONADA: ${emissora.label}</h1>`,
        allowOutsideClick: false,
        cancelButtonText: 'OK',
        showConfirmButton: false,
      };
      dispatch(show(options));
    }
  }, [dispatch, emissora.label, mapaId]);

  useEffect(() => {
    setLoadingCliente(true);
    setMapas([]);
    setSelectedCliente({} as ClienteItem);
    setSelectedMapa({});
    let clienteId = 0;
    if (mapaId) {
      // Busca mapa informado no params (popula card 3)
      api
        .get<MapaMidiaItem>(`/eleicoes/mapademidia/${mapaId}`)
        .then(response => {
          if (response && response.data) {
            setSelectedMapa(response.data);
            clienteId = response.data.cliente?.id || 0;
          }

          // Define selected Cliente
          setSelectedCliente(response.data.cliente || ({} as ClienteItem));
          setIsBloco(response.data.tipo === 'bloco');
          setActiveIndex(response.data.tipo === 'bloco' ? 2 : 1);
          // Busca demais mapas deste cliente (popula card 2)
          api
            .get<MapaMidiaItem[]>('/eleicoes/mapademidia', {
              params: {
                cliente_id: clienteId,
                emissora_id: emissora.value,
                tipo: response.data.tipo,
              },
            })
            .then(response => {
              if (response && response.data) setMapas(response.data);
              else toast.error('Falha de comunicação com o sistema');
            });
        })
        .catch(error => {
          if (error instanceof Error) toast.error(error.message);
          else console.log(error);
        });
    }
    // Buscando demais clientes (popula card 1)
    api
      .get<ClienteItem[]>('/clientes', { params: { politico: true } })
      .then(response => {
        if (response && response.data) {
          setFilteredClientes(response.data);
          setClientes(response.data);
        }
      })
      .catch(error => {
        if (error instanceof Error) toast.error(error.message);
        else console.log(error);
      })
      .finally(() => {
        setLoadingCliente(false);
      });
  }, [emissora.value, mapaId]);

  return (
    <Container>
      <header>
        <h6>MAPA DE MIDIA</h6>

        <Tabs $activeIndex={activeIndex}>
          <button
            onClick={() => {
              handleIsBloco(false);
            }}
          >
            Inserções
          </button>
          <button
            onClick={() => {
              handleIsBloco(true);
            }}
          >
            Bloco
          </button>
        </Tabs>
      </header>
      <Content>
        <Card>
          <Form ref={searchFormRef} onSubmit={handleSearch}>
            <Input
              name="search"
              placeholder="Busca PARTIDO/COLIGAÇÃO"
              icon={<FiSearch />}
            />
          </Form>
          <PerfectScrollbar>
            <ul>
              {!loadingCliente &&
                filteredClientes.length > 0 &&
                filteredClientes.map(cliente => (
                  <li
                    className={
                      cliente.id === selectedCliente.id ? `active` : ``
                    }
                    key={cliente.id}
                    onClick={() => handleSelectClient(cliente)}
                  >
                    <p>{cliente.mnemonico}</p> <FiChevronRight />
                  </li>
                ))}
            </ul>
          </PerfectScrollbar>
        </Card>
        <Card>
          {!!selectedCliente.id && (
            <>
              <header>
                <h6>{selectedCliente.mnemonico}</h6>
              </header>
              <Button onClick={handleNewMapa}>
                Cadastrar novo mapa <FiPlus />
              </Button>
              {!loadingMapas && (
                <PerfectScrollbar>
                  <ul>
                    {mapas.length > 0 &&
                      mapas.map((mapa, index) => (
                        <MapaList
                          hasInsercao={
                            mapa.insercoes ? mapa.insercoes.length > 0 : false
                          }
                          disabled={!mapa.ativo}
                          key={index}
                          className={
                            mapa.id === selectedMapa.id ? `active` : ``
                          }
                          onClick={() => handleSelectMapa(mapa)}
                        >
                          <div>
                            <strong>
                              {mapa.datainicio} - {mapa.datafim}
                            </strong>
                            <p>{mapa.cargo}</p>
                          </div>
                          <span>{mapa.originalfilename}</span>
                          <FiChevronRight />
                        </MapaList>
                      ))}
                  </ul>
                </PerfectScrollbar>
              )}
            </>
          )}
        </Card>

        <Card>
          <>
            {!selectedMapa.new && selectedMapa.id && (
              <>
                <header>
                  <h6>EDITAR </h6>
                  <Button
                    id="trashbutton"
                    fill
                    color="font"
                    round
                    onClick={() => handleDisableMapa(selectedMapa)}
                    disabled
                  >
                    <FiTrash2 />
                  </Button>
                  <Button
                    id={`id_duplicar_${selectedMapa.id}`}
                    fill
                    color="font"
                    round
                    onClick={() => handleCopy(selectedMapa)}
                  >
                    <FiCopy />
                  </Button>
                  <Tooltip target={`id_duplicar_${selectedMapa.id}`}>
                    DUPLICAR
                  </Tooltip>
                </header>
                <FormView
                  mapaMidia={selectedMapa}
                  cliente={selectedCliente}
                  isBloco={isBloco}
                />
              </>
            )}

            {selectedMapa.new && !selectedMapa.id && (
              <>
                <header>
                  <h6>NOVO </h6>
                </header>
                <Form
                  ref={mapaFormRef}
                  initialData={initialData}
                  onSubmit={handleSubmit}
                >
                  <PerfectScrollbar>
                    <section>
                      <label> INFORMAÇÕES DO MAPA DE MIDIA</label>
                      <div className="row">
                        <label>Partido/Coligação</label>
                        <p>{selectedCliente.mnemonico}</p>
                      </div>
                      <div className="row">
                        <label>Cargo</label>
                        <Select
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          searchable
                          name="cargo"
                          options={Cargos.filter(
                            c =>
                              c.isEleicoesMunicipais === isEleicoesMunicipais,
                          )}
                          placeholder="Selecione..."
                          type="threeOptions"
                        />
                      </div>
                      <div className="row">
                        <label>Data/hora recebimento</label>
                        <DatePicker
                          name="recebido_at"
                          placeholderText="ENTREGA DO MAPA"
                          dateFormat="dd/MM/yyyy HH:mm"
                          showTimeSelect
                          maxDate={new Date()}
                          disabled={false}
                        />
                      </div>
                      {isBloco && (
                        <>
                          <div className="row">
                            <label>Título</label>
                            <Input name="titulo" />
                          </div>
                          <div className="row">
                            <label>Observação</label>
                            <TextArea
                              placeholder="Observação"
                              name="observacao"
                              height={50}
                              disable={false}
                              errorField=""
                              icon=""
                            />
                          </div>
                        </>
                      )}
                    </section>
                    <section>
                      <label>MAPA DE MIDIA</label>
                      <ReactDropzoneInput name="file" />
                    </section>
                    <section>
                      <label> DATA DE EXIBIÇÃO</label>
                      {isBloco ? (
                        <div className="calendario">
                          <div>
                            <label>TARDE</label>
                            <Calendar
                              numberOfMonths={1}
                              multiple
                              onChange={(t: DateObject[]) =>
                                setSelectedDatesTarde(
                                  t.map(
                                    value =>
                                      `${value.year}-${value.month}-${value.day}`,
                                  ),
                                )
                              }
                              minDate={new Date()}
                              shadow={false}
                              hideWeekDays={false}
                              className="bg-dark"
                              mapDays={({ date }) => {
                                const props: any = { className: '' };
                                const isWeekend = [0, 6].includes(
                                  date.weekDay.index,
                                );

                                if (isWeekend)
                                  props.className = 'highlight highlight-red';

                                return props;
                              }}
                            />
                          </div>
                          <div>
                            <label>NOITE</label>
                            <Calendar
                              numberOfMonths={1}
                              multiple
                              onChange={(t: DateObject[]) =>
                                setSelectedDatesNoite(
                                  t.map(
                                    value =>
                                      `${value.year}-${value.month}-${value.day}`,
                                  ),
                                )
                              }
                              minDate={new Date()}
                              shadow={false}
                              hideWeekDays={false}
                              className="bg-dark"
                              mapDays={({ date }) => {
                                const props: any = { className: '' };
                                const isWeekend = [0, 6].includes(
                                  date.weekDay.index,
                                );

                                if (isWeekend)
                                  props.className = 'highlight highlight-red';

                                return props;
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <Calendar
                          numberOfMonths={2}
                          multiple
                          onChange={(t: DateObject[]) =>
                            setSelectedDates(
                              t.map(
                                value =>
                                  `${value.year}-${value.month}-${value.day}`,
                              ),
                            )
                          }
                          minDate={addDays(new Date(), 1)}
                          shadow={false}
                          hideWeekDays={false}
                          className="bg-dark"
                          mapDays={({ date }) => {
                            const props: any = { className: '' };
                            const isWeekend = [0, 6].includes(
                              date.weekDay.index,
                            );

                            if (isWeekend)
                              props.className = 'highlight highlight-red';

                            return props;
                          }}
                        />
                      )}
                    </section>
                    <Button
                      className="submitButton"
                      type="submit"
                      color="primary"
                    >
                      SALVAR
                    </Button>
                  </PerfectScrollbar>
                </Form>
              </>
            )}
          </>
        </Card>
      </Content>
    </Container>
  );
}
